import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@kcftech/design-system';
import {
    ApplicationRoleCode,
    CorporationJoinMode,
} from '@kcftech/prometheustypescriptapiclient';
import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { DocumentTitle } from 'components/DocumentTitle';
import { TablePageLayout } from 'components/page/styled';
import { usePermissions } from 'contexts/PermissionsContext';
import { usePagedUsersDataSource } from 'custom_hooks/data_hooks/data_source/usersDataSource';
import { useValueAsTuple } from 'custom_hooks/useValueAsTuple';
import { SearchCreateButtonContainer } from 'pages/Settings/shared/styled';
import { CreateUsersModal } from 'pages/Settings/Users/CreateUsersModal';
import { SingleUserPage } from 'pages/Settings/Users/SingleUserPage';
import { UsersTable } from 'pages/Settings/Users/UsersTable';
import { useEffect, useState } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { ContextualPageProps } from 'utils/types';

export function UsersPage({ corporation, location }: ContextualPageProps) {
    const { canAddUsers } = usePermissions();
    const [nameSearch, setNameSearch] = useState('');
    const [addModalOpen, setAddModalOpen] = useState(false);
    const { url } = useRouteMatch();

    const corporationIds = useValueAsTuple(
        location?.Id === undefined ? corporation?.Id : undefined
    );
    const locationIds = useValueAsTuple(location?.Id);

    const dataSource = usePagedUsersDataSource({
        name: 'users-table',
        defaultFilter: {
            corporationIds,
            corporationJoinMode: CorporationJoinMode.CanViewWithin,
            locationIds,
            nameSearch: '',
        },
    });

    const { setFilter, reloadPage } = dataSource;

    const onUsersCreate = () => {
        setAddModalOpen(false);
        reloadPage();
    };

    useEffect(() => {
        setFilter((currentFilter) => ({
            ...currentFilter,
            corporationIds,
            locationIds,
            nameSearch,
            applicationRoleCodes:
                corporationIds || locationIds ? [ApplicationRoleCode.User] : undefined,
        }));
    }, [corporationIds, locationIds, nameSearch, setFilter]);

    return (
        <Switch>
            <Route path={`${url}/:userId`}>
                <SingleUserPage />
            </Route>
            {/* @ts-expect-error: type issue with older version of react-router-dom */}
            <Route default>
                <TablePageLayout>
                    <DocumentTitle
                        title={`${
                            location?.Name ?? corporation?.Name ?? 'Global'
                        } Users Settings`}
                    />
                    <SearchCreateButtonContainer>
                        <DebouncedSearchInput
                            placeholder="Search"
                            value={nameSearch}
                            onChange={setNameSearch}
                        />
                        {canAddUsers && (
                            <Button
                                appearance="primary"
                                spacing="medium"
                                icon={<FontAwesomeIcon icon={faPlus} />}
                                onClick={() => setAddModalOpen(true)}
                            >
                                Add
                            </Button>
                        )}
                        {addModalOpen && (
                            <CreateUsersModal
                                corporation={corporation}
                                location={location}
                                onCreate={onUsersCreate}
                                onClose={() => setAddModalOpen(false)}
                            />
                        )}
                    </SearchCreateButtonContainer>
                    <UsersTable
                        dataSource={dataSource}
                        corporationId={corporation?.Id}
                        locationId={location?.Id}
                    />
                </TablePageLayout>
            </Route>
        </Switch>
    );
}
